import { ChampionshipEditTab } from 'modules/championship/domain/enums/championship-edit-tab';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { useFeatureFlag } from 'shared/feature-flags';
import Modal from 'ud-ui/components/modal';
import ApplicationsTeamScreen from '../components/applications-v2/applications-team-screen';
import ApplicationsTeamsScreen from '../components/applications-v2/applications-teams-screen';
import ChampionshipApplications from '../components/championship-teams/championship-applications';
import LegacyChampionshipApplications from '../components/championship-teams-legacy/championship-applications';
import LegacyTeamPlayersPage from '../components/championship-teams-legacy/team-players-page';
import TeamPlayersPage from './team-players-page';

type ChampionshipApplicationsPageProps = {
  championshipId: number;
};

/**
 * Вкладка «Участники» соревнования — развилка флоу.
 *
 * Адреса у всех одинаковые (`/championships/:id/applications[/:teamId]`), различается
 * только набор экранов: флаг `applicationsFlowV2` включает второй вариант тем, кто
 * зарегистрировался после даты отсечки, либо тем, кому его включили вручную (`?flow=v2`);
 * внутри «первого» флоу отдельный флаг `applicationsFlow1447` переключает между версией
 * этой ветки (модалка состава команды) и легаси-версией продакшена (состав — отдельная
 * страница) — см. план `applications-flow-1447-ab-toggle`. Оба флага по умолчанию выключены.
 *
 * В `isFlowV2`/`applicationsFlow1447`-версии список команд рендерится всегда, а `:teamId` —
 * отдельным `<Routes>` рядом (не вместо): когда он матчится, состав команды открывается
 * модалкой поверх списка, который остаётся смонтированным и виден за затемнением. Легаси-
 * версия так не может — там состав это отдельная страница, а не оверлей, поэтому её `<Routes>`
 * выбирает между списком и составом, а не показывает их одновременно.
 */
const ChampionshipApplicationsPage = ({ championshipId }: ChampionshipApplicationsPageProps) => {
  const navigate = useNavigate();
  const isFlowV2 = useFeatureFlag('applicationsFlowV2');
  const isFlow1447 = useFeatureFlag('applicationsFlow1447');

  const handleClose = useCallback(() => {
    navigate(Pages.CHAMPIONSHIPS.builders.edit(championshipId, ChampionshipEditTab.TEAMS));
  }, [championshipId, navigate]);

  if (!isFlowV2 && !isFlow1447) {
    return (
      <Routes>
        <Route path=":teamId" element={<LegacyTeamPlayersPage />} />
        <Route path="*" element={<LegacyChampionshipApplications championshipId={championshipId} />} />
      </Routes>
    );
  }

  return (
    <>
      {isFlowV2 ? (
        <ApplicationsTeamsScreen championshipId={championshipId} />
      ) : (
        <ChampionshipApplications championshipId={championshipId} />
      )}

      <Routes>
        <Route
          path=":teamId"
          element={
            <Modal show onClose={handleClose} width={1140} height={isFlowV2 ? undefined : 720}>
              {isFlowV2 ? <ApplicationsTeamScreen championshipId={championshipId} /> : <TeamPlayersPage />}
            </Modal>
          }
        />
      </Routes>
    </>
  );
};

export default ChampionshipApplicationsPage;
